import type {
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnState,
  FillOperationParams,
  GetRowIdParams,
  GridReadyEvent,
  GridSizeChangedEvent,
  SortChangedEvent,
} from "ag-grid-community";
import { type GridApi, type IRowNode, type ProcessCellForExportParams } from "ag-grid-community";
import { AgGridReact, type AgGridReactProps } from "ag-grid-react";
import { clsx } from "clsx";
import { debounce } from "lodash-es";
import { PureComponent } from "react";
import { LoadingBar, parseDateTimeToISO, parseDateToISO, themeSelector } from "widgets";

import { createColumnGroups, preventColumnPinning } from "@/app-utils/grid.ts";
import {
  cloneSelectionModel,
  computeSelectionRange,
  emptySelectionModel,
  type ISelectionModel,
  isSelected,
  resetSelection,
  setAllSelected,
  setSelected,
  type TGridSelModelIdentifier,
} from "@/app-utils/selection-model.ts";
import type { IStringAnyMap } from "@/app-utils/type.ts";
import type { Attribute, InputTableQuerySpec, SortData } from "@/graphql.ts";
import { editDataHasValidationError, type IEditable, isCellReadOnly } from "@/redux/modules/data-editing.ts";
import type { IIdentifiable, ITableColumn } from "@/redux/modules/table-data.ts";
import type { SEQUENCING_TYPE } from "@/settings/settings.ts";
import {
  CELL_TYPE,
  DEFAULT_MIN_COLUMN_WIDTH,
  HEADER_GROUP_HEIGHT,
  ROW_HEIGHT,
  SORT_ORDER_ASC,
} from "@/settings/settings.ts";
import type { IToolsAdminContext } from "@/ui-modules/admin/AdminToolsPage.tsx";
import type { IUserGroupAdminContext } from "@/ui-modules/content-browser/ContentBrowserTableView.js";

import { checkboxDummyColumn, getCellRenderer, lockedDummyColumn } from "./cell-renderer-factory.js";
import { CheckboxHeaderCell, type CheckboxHeaderCellProps } from "./cells/headers/CheckboxHeaderCell.js";
import { HeaderCell, type HeaderCellProps } from "./cells/headers/HeaderCell.js";
import { defaultValueFormatter, defaultValueGetter, gridColumnTypes } from "./column-props-factory.js";
import type { IColumnDef, ICustomColDef } from "./DataTable.js";

export interface IGridContextProps {
  adminContext?: IToolsAdminContext;
  userGroupAdminContext?: IUserGroupAdminContext;
  attributesForDecoration?: Attribute[];
  editMode?: boolean;
  errorMode?: boolean;
  tableSpec?: InputTableQuerySpec;
  expansionPath?: string[];
  /**
   * Used to identify the sequencing type of a genome in the summary page context
   * See SEL11-806
   */
  genomeSequencingType?: keyof typeof SEQUENCING_TYPE;
}

export interface IInMemoryTableWithSelectionModelProps {
  // special
  isLoading?: boolean;
  uniqueGridIdentifier: string; // in order to make the checkbox header cell unique, otherwise clicking there might impact other similar grids
  noRowsMessage?: string;
  scrollToId?: string;
  noRowFlash?: boolean;

  // content
  columns: Partial<ITableColumn>[];
  rowGridData: IIdentifiable[];

  // cells renderers
  cellRendererParamsLookup?: IStringAnyMap; // in order to provide to the cell some additional data

  // selection model (with drag and drop)
  useSelectionModel: boolean;
  initialSelectionModel?: ISelectionModel;
  // getRowId: (node: RowNode) => Array<TGridSelModelIdentifier>; // TODO Use that instead of [].map => {id, data} for rowGridData
  selectionModelUpdated?: (selectionModel: ISelectionModel) => void;
  exclusiveSelection?: boolean;
  allowRowDragging?: boolean; // in this case the column definition should disallow the sorting (sortable: false) ; still supported if sorting is enabled but dragging will be disabled when sorting is enabled, see ag-grid doc
  // isDraggable?: (row: IIdentifiable) => boolean;
  // isSelectable?: (row: IIdentifiable) => boolean;
  allowRowSelection?: boolean;
  // the locked column is currently only used for sample tables
  showLockedColumn?: boolean;

  // callbacks
  gridContext?: IGridContextProps; // hand over to the Ag-grid component
  // onSelectionChange: (???) => void; // @see updateSelectionModel to define callback
  onSortModified?: (sortedColumns: Partial<ITableColumn>[]) => void;
  sortingField?: string | null;
  sortingDirection?: string | null;

  // other
  gridProps?: AgGridReactProps;
  className?: string;
  editingTheme?: boolean;
  headerComponentParams?: HeaderCellProps;
}

interface IInMemoryTableWithSelectionModelState {
  columnDefs: IColumnDef[];
  scrollToRowId: string | undefined | null;
  noRowFlash?: boolean;
  defaultColDefs: ColDef<IEditable>;
}

export default class InMemoryTableWithSelectionModel extends PureComponent<
  IInMemoryTableWithSelectionModelProps,
  IInMemoryTableWithSelectionModelState
> {
  // references to grid and checkbox object
  private gridApi?: GridApi;
  private checkboxHeaderCell?: CheckboxHeaderCell;
  private selectionModel = cloneSelectionModel(emptySelectionModel);

  private isComponentUnmounting = false; // protection against refresh while component is unmounting, since we use a setTimeout to refresh... see  [SEL5-1218, SEL5-1138]
  private clientWidth?: number; // check if grid size changed

  constructor(props: IInMemoryTableWithSelectionModelProps) {
    super(props);
    this.state = {
      columnDefs: [],
      scrollToRowId: props.scrollToId,
      noRowFlash: props.noRowFlash,
      defaultColDefs: {},
    };
  }

  getSelectedIds = () => this.selectionModel.selectedIds;

  onSortChanged = (sortEvent: SortChangedEvent) => {
    const sortedColumns: Partial<ITableColumn>[] = []; // for the onSortModified callback
    // get the grid sorting and translate into our own model that understands how to show our custom icons
    sortEvent.api.getColumnState().forEach((columnState: ColumnState) => {
      const colDef = this.gridApi?.getColumnDef(columnState.colId) as ICustomColDef;
      const sortData: SortData = InMemoryTableWithSelectionModel.buildSortDataFromColumnState(columnState);
      colDef.sorting = sortData;
      if (sortData.priority) {
        sortedColumns.push({ field: colDef.field, sort: sortData } satisfies Partial<ITableColumn>);
      }
    });
    // refresh the header cells only
    this.gridApi?.refreshHeader();
    // [SEL9-1436] also redraw the rows otherwise the rowIndex might be out of sync and therefore the range selection might be buggy
    this.gridApi?.redrawRows();

    if (this.props.onSortModified) {
      this.props.onSortModified(sortedColumns);
    }
  };

  resetSelection = () => {
    resetSelection(this.selectionModel);
    this.updateSelectionModel();
  };

  sizeColumnsToFit = () => (this.gridApi ? this.gridApi.sizeColumnsToFit() : undefined);

  static buildSortDataFromColumnState(columnState: ColumnState) {
    const sortData: SortData = {
      direction: columnState.sort?.toUpperCase(),
      priority: columnState.sortIndex != null ? columnState.sortIndex + 1 : null,
    };
    return sortData;
  }

  updateSelectionModel = () => {
    // Update ag-grid selection model because this is the only way to set .ag-row-selected for selected table rows
    if (this.gridApi) {
      const nodesToSelect: IRowNode[] = [];
      const nodesToDeselect: IRowNode[] = [];
      this.gridApi.forEachNode((node) => {
        if (node.data) {
          const selected = isSelected(this.selectionModel, node.data.id);
          if (selected && !node.isSelected()) {
            nodesToSelect.push(node);
          } else if (!selected && node.isSelected()) {
            nodesToDeselect.push(node);
          }
        }
      });
      this.gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
      this.gridApi.setNodesSelected({ nodes: nodesToDeselect, newValue: false });

      // need to redraw the rows to see the updated selection
      this.gridApi.refreshCells({ force: true }); // seems to be required to draw the checkboxes
    }

    this.updateCheckboxHeaderCell();

    if (this.props.selectionModelUpdated) {
      this.props.selectionModelUpdated(this.selectionModel);
    }
  };

  registerCheckboxHeaderCellReference = (component: CheckboxHeaderCell) => {
    this.checkboxHeaderCell = component;

    this.updateCheckboxHeaderCell();
  };

  handleSelectAll = (selected: boolean) => {
    // update the local selection model
    setAllSelected(this.selectionModel, this.getAllFilteredSortedIds(), selected);
    // update the agGrid inner selection model accordingly in order to have checkboxes and selection styling in line
    this.updateSelectionModel();
  };

  getAllFilteredSortedIds = (): TGridSelModelIdentifier[] => {
    const allIds: TGridSelModelIdentifier[] = [];
    this.gridApi?.forEachNodeAfterFilterAndSort((rowNode) => {
      const rowData: IIdentifiable = rowNode.data;
      if (rowData.isSelectable !== false /* undefined means true */) {
        allIds.push(rowData.id);
      }
    });
    return allIds;
  };

  updateCheckboxHeaderCell = () => {
    // Set toggle checkbox
    if (this.checkboxHeaderCell) {
      const ids = this.getAllFilteredSortedIds();
      const selectableItemsCount = ids.length;
      this.checkboxHeaderCell.setSelected(
        selectableItemsCount !== 0 && this.selectionModel.selectedIds.length >= selectableItemsCount,
      );
    }
  };

  handleRowSelectionChange = (
    rowIndex: number, // the index of the sorted row in the data model, i.e., this.props.rowGridData
    rowId: TGridSelModelIdentifier,
    selected: boolean,
    rangeCheck: boolean,
  ) => {
    if (rangeCheck && !this.props.exclusiveSelection) {
      const { rangeStart, rangeStop } = computeSelectionRange(this.selectionModel, rowIndex);
      for (let pos = rangeStart; pos <= rangeStop; pos++) {
        const rowData: IIdentifiable = this.gridApi?.getDisplayedRowAtIndex(pos)?.data;
        if (rowData && rowData.isSelectable !== false) {
          setSelected(this.selectionModel, rowData.id, pos, selected);
        }
      }
    } else {
      setSelected(this.selectionModel, rowId, rowIndex, selected, this.props.exclusiveSelection);
    }

    this.updateCheckboxHeaderCell();

    this.updateSelectionModel();
  };

  // creation of a dedicated column with a grab for row dragging ; the text shown while dragging corresponds to the "field" property below,
  // which is by convention the first column. However that seems to not be needed if row dragging has animation, and as mentioned in SEL6-1033
  // it might cause some issues regarding the column sorting since the same "field" value will be set twice
  createRowDraggingColumnDef = () => ({
    field: "row-dragging", // if set to another field name, the value of this field will be shown during the dragging of rows (if no animation)
    headerName: "",
    maxWidth: 24,
    minWidth: 24,
    pinned: true,
    rowDrag: true,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    suppressResize: true,
    width: 20,
  });

  createCheckboxColumnDef = () => {
    const checkboxCellRenderer = getCellRenderer(checkboxDummyColumn);

    const headerCellProps: CheckboxHeaderCellProps = {
      onChange: this.handleSelectAll,
      defaultChecked: false,
      uniqueGridIdentifier: this.props.uniqueGridIdentifier,
      setCheckboxRef: this.registerCheckboxHeaderCellReference,
    };

    return {
      ...gridColumnTypes[CELL_TYPE.CHECKBOX],
      cellRenderer: checkboxCellRenderer,
      cellRendererParams: {
        onChange: this.handleRowSelectionChange,
      },
      /** The custom header component to be used for rendering the component header in the hosting framework (ie: React/Angular). If none specified the default ag-Grid is used**/
      headerComponent: !this.props.exclusiveSelection ? (CheckboxHeaderCell as any) : null,
      /** The custom header component parameters**/
      headerComponentParams: headerCellProps,
      suppressFillHandle: true,
    };
  };

  createLockedColumnDef = () => {
    const lockedCellRenderer = getCellRenderer(lockedDummyColumn);
    return {
      ...gridColumnTypes[CELL_TYPE.CHECKBOX],
      cellRenderer: lockedCellRenderer,
      headerComponent: null,
      suppressFillHandle: true,
      editable: () => false,
      maxWidth: 28,
      minWidth: 28,
      width: 28,
    };
  };

  getColumnDefs = (): IColumnDef[] => {
    return createColumnGroups({
      tableColumns: this.props.columns,
      tableQuerySpec: {},
      createCheckboxColumnDef: this.props.useSelectionModel && this.createCheckboxColumnDef,
      createRowDraggingColumnDef: this.props.allowRowDragging && this.createRowDraggingColumnDef,
      createLockedColumnDef: this.props.showLockedColumn && this.createLockedColumnDef,
      cellRendererParamsLookup: this.props.cellRendererParamsLookup,
      supportClientSorting: true,
      gridContext: this.props.gridContext,
    });
  };

  adaptSortModel = () => {
    if (this.props.sortingField && this.gridApi) {
      const sortingColumn = this.props.columns.find(
        (column: Partial<ITableColumn>) => column.field === this.props.sortingField,
      );

      this.gridApi.applyColumnState({
        state: [
          {
            colId: this.props.sortingField,
            // take the first option of the sorting sequence, if provided ; otherwise fallback to ascending sort
            sort:
              this.props.sortingDirection?.toLowerCase() ??
              sortingColumn?.sortingOrder?.[0] ??
              SORT_ORDER_ASC.toLowerCase(),
            sortIndex: 0,
          },
        ],
        defaultState: { sort: null, sortIndex: null },
      });
    }
  };

  onGridReady = (params: GridReadyEvent) => {
    if (this.isComponentUnmounting) {
      return;
    }

    this.gridApi = params.api;

    if (this.props.initialSelectionModel) {
      this.updateSelectionModel();
    }
    if (this.props.isLoading) {
      this.gridApi.setGridOption("loading", true);
    }
  };

  onColumnResized = (event: ColumnResizedEvent) => {
    if (this.gridApi && event.finished) {
      this.gridApi.resetRowHeights();
    }
  };

  debouncedSizeColumnsToFit = debounce(() => {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }
  }, 200);

  onGridSizeChanged = (params: GridSizeChangedEvent) => {
    if (this.gridApi) {
      // potentially first sizeColumnsToFit because was hidden before this stage and therefore had 0 width (e.g. tabs)
      if (this.clientWidth == null) {
        this.clientWidth = params.clientWidth;
        this.gridApi.sizeColumnsToFit();
      }

      if (
        // the size might be 0 when the grid is hidden (e.g. using tabs)
        params.clientWidth > 0 &&
        this.clientWidth > 0 &&
        params.clientWidth !== this.clientWidth
      ) {
        this.clientWidth = params.clientWidth;
        this.debouncedSizeColumnsToFit();
      }
    }
  };

  onColumnMoved = (params: ColumnMovedEvent) => {
    const fixedFixedColumns = this.props.columns.findIndex((column: Partial<ITableColumn>) => !column.fixed);
    const totalFixedColCount =
      fixedFixedColumns +
      (this.props.allowRowDragging ? (this.props.useSelectionModel ? 2 : 1) : this.props.useSelectionModel ? 1 : 0);
    preventColumnPinning(params, totalFixedColCount);
  };

  componentDidMount() {
    if (this.props.initialSelectionModel) {
      this.selectionModel = this.props.initialSelectionModel;
    }
    this.setState({
      columnDefs: this.getColumnDefs(),
      // defaultColDefs should have a stable reference, otherwise the grid columns do resize each time this ref changes ! (happened after update to ag-grid v28.0.2 / ag-grid-react v28.0.0)
      defaultColDefs: {
        cellClassRules: {
          "edit-data-validation-error": this.props.gridContext?.editMode && editDataHasValidationError,
          "edit-data-readonly-column": this.props.gridContext?.editMode && isCellReadOnly,
        },
        sortable: true,
        minWidth: DEFAULT_MIN_COLUMN_WIDTH,
        valueGetter: defaultValueGetter,
        valueFormatter: defaultValueFormatter, // by default the formatted value of an array is a joined string ; since the generic TableCellText takes into account the formattedValue we rather need the raw data
        headerComponent: HeaderCell,
        resizable: true,
        headerComponentParams: {
          hideMenu: true,
          ...this.props.headerComponentParams,
        },
        autoHeaderHeight: true,
      },
    });
  }

  componentDidUpdate(prevProps: IInMemoryTableWithSelectionModelProps) {
    if (prevProps.columns !== this.props.columns) {
      this.setState({ columnDefs: this.getColumnDefs() });
    }

    if (prevProps.sortingField !== this.props.sortingField) {
      this.adaptSortModel();
    }

    if (this.state.scrollToRowId) {
      this.ensureEntityIsVisible(this.state.scrollToRowId);
    }

    if (prevProps.isLoading !== this.props.isLoading) {
      if (this.props.isLoading) {
        this.gridApi?.setGridOption("loading", true);
      } else {
        this.gridApi?.setGridOption("loading", false);
      }
    }
  }

  componentWillUnmount() {
    this.isComponentUnmounting = true;
  }

  ensureEntityIsVisible = (targetId: string | number) => {
    if (this.gridApi && !this.props.isLoading && this.props.rowGridData) {
      const targetIndex: number = this.props.rowGridData.findIndex(
        (row: IIdentifiable) => String(row.id) === String(targetId),
      );
      if (targetIndex > -1) {
        setTimeout(() => {
          // make sure ag-grid did layout the rows already
          this.gridApi?.ensureIndexVisible(targetIndex, null);
          const rowNode = this.gridApi?.getRowNode(targetId as string);
          if (rowNode && !this.state.noRowFlash) {
            this.gridApi?.flashCells({ flashDuration: 2000, rowNodes: [rowNode] });
          }
          this.setState({ scrollToRowId: null });
        }, 500);
      }
    }
  };

  getRowId = (params: GetRowIdParams<IIdentifiable>) => params.data.id as string;

  processCellFromClipboard = (params: ProcessCellForExportParams) => {
    if (params.column.getColDef().type === CELL_TYPE.DATE) {
      return parseDateToISO(params.value);
    } else if (params.column.getColDef().type === CELL_TYPE.DATE_TIME) {
      return parseDateTimeToISO(params.value);
    } else {
      return params.value;
    }
  };

  processCellForClipboard = (params: ProcessCellForExportParams) => {
    if (params.column.getColDef().type === CELL_TYPE.FILENAME) {
      // file or folder
      return params.value.fileName;
    } else {
      // contents should be fine
      return params.value;
    }
  };

  fillOperation = (params: FillOperationParams) => {
    if (params.column.getColDef().type === CELL_TYPE.DATE) {
      return parseDateToISO(params.initialValues[0]);
    } else if (params.column.getColDef().type === CELL_TYPE.DATE_TIME) {
      return parseDateTimeToISO(params.initialValues[0]);
    } else {
      return false;
    }
    // return params.currentCellValue // use this to skip filling a specific column
  };

  render() {
    return (
      <div
        className={clsx("fill", "data-table-grid", this.props.className, {
          "ag-theme-selector-grid": !this.props.editingTheme,
          "ag-theme-selector-grid-editing": this.props.editingTheme,
        })}
      >
        <LoadingBar isLoading={this.props.isLoading ?? false} />
        <AgGridReact
          componentWrappingElement="div"
          columnDefs={this.state.columnDefs}
          columnTypes={gridColumnTypes}
          context={this.props.gridContext}
          singleClickEdit={true}
          suppressClickEdit={false}
          stopEditingWhenCellsLoseFocus={true}
          suppressLastEmptyLineOnPaste={true}
          processCellForClipboard={this.processCellForClipboard}
          processCellFromClipboard={this.processCellFromClipboard}
          defaultColDef={this.state.defaultColDefs}
          getRowId={this.getRowId}
          rowData={this.props.isLoading && this.props.rowGridData.length === 0 ? null : this.props.rowGridData} // change rowGridData so that the overlayNoRowsTemplate is refreshed
          // settings
          animateRows={this.props.allowRowDragging}
          groupHeaderHeight={HEADER_GROUP_HEIGHT}
          headerHeight={HEADER_GROUP_HEIGHT}
          rowDragManaged={true}
          rowHeight={ROW_HEIGHT}
          rowSelection={{
            mode: "multiRow",
            checkboxes: false,
            headerCheckbox: false,
            enableClickSelection: false,
            copySelectedRows: true,
          }}
          cellSelection={
            this.props.editingTheme
              ? {
                  handle: {
                    mode: "fill",
                    setFillValue: this.fillOperation,
                  },
                  suppressMultiRanges: true,
                }
              : false
          }
          suppressContextMenu={true}
          suppressMovableColumns={false}
          suppressMultiSort={false}
          // callbacks
          onColumnMoved={this.onColumnMoved}
          onColumnResized={this.onColumnResized}
          onFilterChanged={() => this.gridApi?.resetRowHeights()} // having row autoHeight enabled might lead to some wrong row height (??)
          onGridReady={this.onGridReady}
          onFirstDataRendered={this.sizeColumnsToFit}
          onGridSizeChanged={this.onGridSizeChanged}
          onSortChanged={this.onSortChanged}
          onRowDataUpdated={this.updateSelectionModel} // e.g. in case filtering has changed the visual selection should be updated accordingly
          onDisplayedColumnsChanged={this.sizeColumnsToFit}
          //onRowClicked={this.props.allowRowSelection === true ? this.selectRow : undefined}
          // special
          // warning: using noRowsOverlayComponentFramework/loadingOverlayComponentFramework does not work well in version 21.2.2 (duplicate label)
          overlayNoRowsTemplate={`<span class="grid-overlay-no-rows">${
            this.props.isLoading && this.props.rowGridData && this.props.rowGridData.length > 0
              ? "Loading data..."
              : this.props.noRowsMessage || "No rows to show"
          }</span>`}
          overlayLoadingTemplate={'<span class="grid-overlay-no-rows">Loading data...</span>'}
          undoRedoCellEditing={true}
          // other
          theme={themeSelector}
          {...this.props.gridProps}
        />
      </div>
    );
  }
}
